import {assert} from 'Asserts';

import {getFieldInfoFromDataFlowEdgeHandle} from '../../flowchartIdentifierUtils';
import {
  CustomMappingData,
  FlowchartStaticEdgeData,
  ProcessFlowchartEdgeType,
} from '../../processFlowchartConfigEditorConstants';
import {
  isStaticRouterNodeData,
  isStaticTaskSchemaNodeData,
  StaticFlowchartNodeData,
} from '../../processFlowchartConfigEditorNodeTypes';
import {
  buildTransitionIndexByPTDVPair,
  createEmptyTransition,
  getFieldPairKey,
  getOrCreateTransition,
  getPTDVPairKey,
  getTransitionId,
} from './transitionKeyUtils';
import {Transition} from './TransitionTypes';

export interface CreateTransitionResult {
  transition: Transition;
  /** Whether a new transition was created */
  isNew: boolean;
}
/**
 * Resolves the source and target PTDV IDs for an edge.
 * When a node has been deleted, its PTDV ID can no longer be found in nodesStaticData.
 * In that case, deletedNodeContext provides the PTDV IDs captured before removal.
 */
export function getPTDVIdsFromEdge(
  edge: FlowchartStaticEdgeData,
  nodes: Array<StaticFlowchartNodeData>,
  deletedNodeContext?: {deletedSourceNodePTDVId?: string; deletedTargetNodePTDVId?: string}
): {sourcePTDVId: string; targetPTDVId: string} | null {
  const sourceNode = nodes.find((n) => n.id === edge.source);
  const targetNode = nodes.find((n) => n.id === edge.target);

  const sourcePTDVId =
    sourceNode && isStaticTaskSchemaNodeData(sourceNode)
      ? sourceNode.taskDefinitionVersionSpec.clientId
      : deletedNodeContext?.deletedSourceNodePTDVId;

  const targetPTDVId =
    targetNode && isStaticTaskSchemaNodeData(targetNode)
      ? targetNode.taskDefinitionVersionSpec.clientId
      : deletedNodeContext?.deletedTargetNodePTDVId;

  if (sourcePTDVId == null || targetPTDVId == null) {
    return null;
  }

  return {sourcePTDVId, targetPTDVId};
}

function getFieldPairKeyFromDataFlowEdge(
  edge: FlowchartStaticEdgeData,
  nodes: Array<StaticFlowchartNodeData>,
  deletedNodeContext?: {deletedSourceNodePTDVId?: string; deletedTargetNodePTDVId?: string}
): string | null {
  if (edge.type !== ProcessFlowchartEdgeType.DATA_FLOW) {
    return null;
  }

  const ptdvIds = getPTDVIdsFromEdge(edge, nodes, deletedNodeContext);
  if (!ptdvIds) {
    return null;
  }

  const sourceHandle = getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle);
  const targetHandle = getFieldInfoFromDataFlowEdgeHandle(edge.targetHandle);

  return getFieldPairKey({
    sourcePTDVId: ptdvIds.sourcePTDVId,
    sourceSectionType: sourceHandle.sectionType,
    sourceFieldClientId: sourceHandle.fieldClientId,
    targetPTDVId: ptdvIds.targetPTDVId,
    targetSectionType: targetHandle.sectionType,
    targetFieldClientId: targetHandle.fieldClientId,
  });
}

export function getOrCreateTransitionForEdge(
  edge: FlowchartStaticEdgeData,
  nodes: Array<StaticFlowchartNodeData>,
  transitions: Record<string, Transition>
): CreateTransitionResult | null {
  if (edge.type !== ProcessFlowchartEdgeType.CONTROL_FLOW) {
    return null;
  }

  const ptdvIds = getPTDVIdsFromEdge(edge, nodes);
  if (!ptdvIds) {
    return null;
  }

  const {sourcePTDVId, targetPTDVId} = ptdvIds;
  const index = buildTransitionIndexByPTDVPair(transitions);
  const pairKey = getPTDVPairKey(sourcePTDVId, targetPTDVId);
  const existingTransitionId = index[pairKey]?.[0];

  if (existingTransitionId != null && transitions[existingTransitionId]) {
    return {
      transition: transitions[existingTransitionId],
      isNew: false,
    };
  }

  const newTransition = createEmptyTransition({
    sourcePTDVIds: [sourcePTDVId],
    targetPTDVId,
  });
  return {
    transition: newTransition,
    isNew: true,
  };
}

export interface OrphanedSourcePTDV {
  transitionId: string;
  sourcePTDVId: string;
}

export interface FindOrphanedTransitionIdsAndNodePairsResult {
  orphanedTransitionIds: Array<string>;
  orphanedSourcePTDV: Array<OrphanedSourcePTDV>;
}

/**
 * Returns transitions to delete and sources to remove after a control flow edge was removed.
 *
 * For merging transitions (transitions with multiple source PTDVs), if all control flow edges
 * for a specific source PTDV are removed but other sources still have edges, that source is
 * returned as an orphaned source to be removed from the transition.
 * If ALL sources lose their edges, the entire transition is orphaned.
 *
 * e.g. if we have a transition {A, B} -> C and remove the edge from A to C (but B -> C remains),
 * the node pair (A, C) is returned as orphaned. If both A -> C and B -> C are removed, the
 * transition itself is orphaned.
 *
 * When a node has been deleted, its PTDV ID can no longer be resolved from nodesStaticData.
 * In that case, the caller passes deletedNodeContext with the PTDV IDs captured before the
 * node was removed, so we can still identify the affected transition.
 */
export function findOrphanedTransitionAndSourcePTDVIds(
  removedEdge: FlowchartStaticEdgeData,
  remainingEdges: Array<FlowchartStaticEdgeData>,
  nodes: Array<StaticFlowchartNodeData>,
  ptdvPairToTransitionIdIndex: Record<string, Array<string>>,
  transitions: Record<string, Transition>,
  deletedNodeContext?: {deletedSourceNodePTDVId?: string; deletedTargetNodePTDVId?: string}
): FindOrphanedTransitionIdsAndNodePairsResult {
  const EMPTY_RESULT: FindOrphanedTransitionIdsAndNodePairsResult = {
    orphanedTransitionIds: [],
    orphanedSourcePTDV: [],
  };

  if (removedEdge.type !== ProcessFlowchartEdgeType.CONTROL_FLOW) {
    return EMPTY_RESULT;
  }

  const ptdvIds = getPTDVIdsFromEdge(removedEdge, nodes, deletedNodeContext);
  if (!ptdvIds) {
    return EMPTY_RESULT;
  }

  const {sourcePTDVId, targetPTDVId} = ptdvIds;
  const pairKey = getPTDVPairKey(sourcePTDVId, targetPTDVId);
  const transitionIds = ptdvPairToTransitionIdIndex[pairKey];
  if (transitionIds == null || transitionIds.length === 0) {
    return EMPTY_RESULT;
  }

  const nodeToPTDVId = new Map<string, string>();
  for (const node of nodes) {
    if (isStaticTaskSchemaNodeData(node)) {
      nodeToPTDVId.set(node.id, node.taskDefinitionVersionSpec.clientId);
    }
  }
  if (deletedNodeContext?.deletedSourceNodePTDVId != null) {
    nodeToPTDVId.set(removedEdge.source, deletedNodeContext.deletedSourceNodePTDVId);
  }
  if (deletedNodeContext?.deletedTargetNodePTDVId != null) {
    nodeToPTDVId.set(removedEdge.target, deletedNodeContext.deletedTargetNodePTDVId);
  }

  const hasEdgeToTarget = (sourceId: string): boolean =>
    remainingEdges.some(
      (edge) =>
        edge.type === ProcessFlowchartEdgeType.CONTROL_FLOW &&
        nodeToPTDVId.get(edge.source) === sourceId &&
        nodeToPTDVId.get(edge.target) === targetPTDVId
    );

  const result: FindOrphanedTransitionIdsAndNodePairsResult = {
    orphanedTransitionIds: [],
    orphanedSourcePTDV: [],
  };

  for (const transitionId of transitionIds) {
    const transition = transitions[transitionId];
    if (!transition) {
      continue;
    }

    const orphanedSourceIds = transition.sourcePTDVIds.filter((sourceId) => !hasEdgeToTarget(sourceId));

    if (orphanedSourceIds.length === transition.sourcePTDVIds.length) {
      result.orphanedTransitionIds.push(transitionId);
    } else {
      for (const orphanedSourceId of orphanedSourceIds) {
        result.orphanedSourcePTDV.push({transitionId, sourcePTDVId: orphanedSourceId});
      }
    }
  }

  return result;
}

/**
 * Find all transitions affected by changes to nodes or edges.
 * Useful for determining which transitions need updates when the flowchart changes.
 */
export function getAffectedTransitions(
  changedNodeIds: Array<string>,
  nodes: Array<StaticFlowchartNodeData>,
  transitions: Record<string, Transition>
): Array<string> {
  if (changedNodeIds.length === 0) {
    return [];
  }

  const changedPTDVIds = new Set<string>();
  for (const nodeId of changedNodeIds) {
    const node = nodes.find((n) => n.id === nodeId);
    if (node && isStaticTaskSchemaNodeData(node)) {
      changedPTDVIds.add(node.taskDefinitionVersionSpec.clientId);
    }
  }

  if (changedPTDVIds.size === 0) {
    return [];
  }

  const affectedTransitionIds: Array<string> = [];
  for (const transition of Object.values(transitions)) {
    const involvesPTDV =
      transition.sourcePTDVIds.some((id) => changedPTDVIds.has(id)) ||
      changedPTDVIds.has(transition.targetPTDVId);

    if (involvesPTDV) {
      affectedTransitionIds.push(transition.id);
    }
  }

  return affectedTransitionIds;
}

export function addCustomMappingForDataFlowEdge(
  edge: FlowchartStaticEdgeData,
  nodes: Array<StaticFlowchartNodeData>,
  transition: Transition,
  customMappingData: CustomMappingData | null = null
): Record<string, CustomMappingData | null> | null {
  const fieldPairKey = getFieldPairKeyFromDataFlowEdge(edge, nodes);
  if (!fieldPairKey) {
    return null;
  }

  return {
    ...transition.customMappingDataByFieldPair,
    [fieldPairKey]: customMappingData,
  };
}

/**
 * Clears the custom mapping for a data flow edge
 */
export function removeCustomMappingForDataFlowEdge(
  edge: FlowchartStaticEdgeData,
  nodes: Array<StaticFlowchartNodeData>,
  transition: Transition
): Record<string, CustomMappingData | null> | null {
  const fieldPairKey = getFieldPairKeyFromDataFlowEdge(edge, nodes);
  if (!fieldPairKey) {
    return null;
  }

  return {
    ...transition.customMappingDataByFieldPair,
    [fieldPairKey]: null,
  };
}

/**
 * Removes a field pair entirely from the transition when a data flow edge is deleted.
 */
export function removeFieldPairForDataFlowEdge(
  edge: FlowchartStaticEdgeData,
  nodes: Array<StaticFlowchartNodeData>,
  transition: Transition,
  deletedNodeContext?: {deletedSourceNodePTDVId?: string; deletedTargetNodePTDVId?: string}
): Record<string, CustomMappingData | null> | null {
  const fieldPairKey = getFieldPairKeyFromDataFlowEdge(edge, nodes, deletedNodeContext);
  if (!fieldPairKey) {
    return null;
  }

  const {[fieldPairKey]: _, ...remainingMappings} = transition.customMappingDataByFieldPair;
  return remainingMappings;
}

/**
 * Removes a source PTDV from a transition and re-keys it in the transitions record.
 * Since transition IDs are derived from sourcePTDVIds + targetPTDVId, removing a source
 * requires updating the ID and the record key to keep the transition reachable.
 */
export function removeSourcePTDVFromTransition(
  transitions: Record<string, Transition>,
  transitionId: string,
  sourcePTDVId: string
): void {
  const transition = transitions[transitionId];
  if (!transition) {
    return;
  }

  transition.sourcePTDVIds = transition.sourcePTDVIds.filter((id) => id !== sourcePTDVId);
  delete transition.transitionSourcesBySourcePTDVId[sourcePTDVId];
  const prefix = `${sourcePTDVId}:`;
  for (const key of Object.keys(transition.customMappingDataByFieldPair)) {
    if (key.startsWith(prefix)) {
      delete transition.customMappingDataByFieldPair[key];
    }
  }

  const newId = getTransitionId(transition.sourcePTDVIds, transition.targetPTDVId);
  if (newId !== transitionId) {
    transition.id = newId;
    transitions[newId] = transition;
    delete transitions[transitionId];
  }
}

/**
 * Creates transitions for a newly added control flow edge.
 * Handles three cases:
 * 1. Direct task-to-task edge: Creates a single transition
 * 2. Router-to-task edge: Creates transitions from all upstream task nodes to the target
 * 3. Task-to-router edge: Creates transitions from the source to all downstream task nodes
 *
 * @returns Array of new transitions to be added (excludes transitions that already exist)
 */
export function createTransitionsForNewControlFlowEdge(
  newEdge: FlowchartStaticEdgeData,
  nodes: Array<StaticFlowchartNodeData>,
  allEdges: Array<FlowchartStaticEdgeData>,
  existingTransitions: Record<string, Transition>
): Array<Transition> {
  if (newEdge.type !== ProcessFlowchartEdgeType.CONTROL_FLOW) {
    return [];
  }

  const sourceNode = nodes.find((n) => n.id === newEdge.source);
  const targetNode = nodes.find((n) => n.id === newEdge.target);

  if (sourceNode == null || targetNode == null) {
    return [];
  }

  const newTransitions: Array<Transition> = [];
  const workingTransitions = {...existingTransitions};

  const addTransitionIfNew = (sourcePTDVId: string, targetPTDVId: string): void => {
    const {transition, isNew} = getOrCreateTransition({
      sourcePTDVIds: [sourcePTDVId],
      targetPTDVId,
      transitions: workingTransitions,
      isPlanned: true,
    });
    if (isNew) {
      for (const edge of allEdges) {
        if (edge.type !== ProcessFlowchartEdgeType.DATA_FLOW) {
          continue;
        }
        const ptdvIds = getPTDVIdsFromEdge(edge, nodes);
        if (ptdvIds?.sourcePTDVId === sourcePTDVId && ptdvIds.targetPTDVId === targetPTDVId) {
          const updatedMappings = addCustomMappingForDataFlowEdge(edge, nodes, transition);
          if (updatedMappings) {
            transition.customMappingDataByFieldPair = updatedMappings;
          }
        }
      }
      newTransitions.push(transition);
    }
  };

  // Case 1: Direct task-to-task edge
  if (isStaticTaskSchemaNodeData(sourceNode) && isStaticTaskSchemaNodeData(targetNode)) {
    addTransitionIfNew(
      sourceNode.taskDefinitionVersionSpec.clientId,
      targetNode.taskDefinitionVersionSpec.clientId
    );
  }

  // Case 2: Router-to-task edge - create transitions from all upstream task nodes
  if (isStaticRouterNodeData(sourceNode) && isStaticTaskSchemaNodeData(targetNode)) {
    const targetPTDVId = targetNode.taskDefinitionVersionSpec.clientId;
    const incomingEdges = allEdges.filter(
      (e) => e.target === sourceNode.id && e.type === ProcessFlowchartEdgeType.CONTROL_FLOW
    );
    assert(incomingEdges.length <= 1, 'Expected at most one incoming edge');
    const upstreamNode = nodes.find((n) => n.id === incomingEdges[0]?.source);
    if (upstreamNode != null && isStaticTaskSchemaNodeData(upstreamNode)) {
      addTransitionIfNew(upstreamNode.taskDefinitionVersionSpec.clientId, targetPTDVId);
    }
  }

  // Case 3: Task-to-router edge - create transitions to all downstream task nodes
  if (isStaticTaskSchemaNodeData(sourceNode) && isStaticRouterNodeData(targetNode)) {
    const sourcePTDVId = sourceNode.taskDefinitionVersionSpec.clientId;
    const outgoingEdges = allEdges.filter(
      (e) => e.source === targetNode.id && e.type === ProcessFlowchartEdgeType.CONTROL_FLOW
    );
    for (const outgoingEdge of outgoingEdges) {
      const downstreamNode = nodes.find((n) => n.id === outgoingEdge.target);
      if (downstreamNode != null && isStaticTaskSchemaNodeData(downstreamNode)) {
        addTransitionIfNew(sourcePTDVId, downstreamNode.taskDefinitionVersionSpec.clientId);
      }
    }
  }

  return newTransitions;
}
